/* WTX first-order case planning. Prices are integer cents, weights are pounds. */
(function (root, factory) {
  'use strict';
  var engine = factory();
  if (typeof module === 'object' && module.exports) module.exports = engine;
  else if (root) root.WTXFirstOrderEngine = engine;
}(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var MAX_CASES = 99;
  var MAX_SAFE = Number.MAX_SAFE_INTEGER;
  var EPSILON = 1e-8;

  function number(value) {
    return typeof value === 'number' && Number.isFinite(value);
  }

  function identifier(value) {
    return (typeof value === 'string' && value.trim().length > 0) ||
      (Number.isSafeInteger(value) && value > 0);
  }

  function empty(error, targetLb, shippingExtraLb) {
    return {
      lines: [],
      targetLb: targetLb || 0,
      totalLb: 0,
      coverageDays: 0,
      subtotal: 0,
      shippingExtraLb: shippingExtraLb || 0,
      error: error
    };
  }

  function better(candidate, incumbent, packSize) {
    if (!incumbent) return true;
    if (packSize === 'value' && candidate.cost !== incumbent.cost) {
      return candidate.cost < incumbent.cost;
    }
    if (Math.abs(candidate.lb - incumbent.lb) > EPSILON) {
      return candidate.lb < incumbent.lb;
    }
    if (packSize === 'small') {
      if (candidate.maxCaseLb !== incumbent.maxCaseLb) {
        return candidate.maxCaseLb < incumbent.maxCaseLb;
      }
      // With the same coverage, more cases means a smaller average pack size.
      if (candidate.count !== incumbent.count) return candidate.count > incumbent.count;
    }
    if (candidate.cost !== incumbent.cost) return candidate.cost < incumbent.cost;
    return candidate.count < incumbent.count;
  }

  function choose(variants, neededLb, packSize) {
    var first = variants[0];
    var second = variants[1];
    var capacity = Math.max(first.lb, second ? second.lb : 0) * MAX_CASES;
    if (neededLb > capacity + EPSILON) return null;
    var best = null;
    for (var a = 0; a <= MAX_CASES; a += 1) {
      var maximumB = second ? MAX_CASES - a : 0;
      for (var b = 0; b <= maximumB; b += 1) {
        if (a + b === 0) continue;
        var lb = a * first.lb + (second ? b * second.lb : 0);
        var cost = a * first.price + (second ? b * second.price : 0);
        if (lb + EPSILON < neededLb || lb > MAX_SAFE || !Number.isSafeInteger(cost)) continue;
        var candidate = {
          quantities: [a, b],
          lb: lb,
          cost: cost,
          count: a + b,
          maxCaseLb: Math.max(a ? first.lb : 0, b && second ? second.lb : 0)
        };
        if (better(candidate, best, packSize)) best = candidate;
      }
    }
    return best;
  }

  /**
   * Build a one-time merchandise estimate, without nutrition or fulfillment claims.
   * variants[].price and lines[].price are unit prices in cents.
   * variants[].lb and lines[].lb are pounds per case.
   * targetLb excludes shipping minimums and case rounding.
   * shippingExtraLb is the extra target needed to reach 40 lb, before case rounding.
   * Invalid/unavailable variants are excluded. Each product supports at most two
   * eligible variants. Each selected product's share must be positive and total 1.
   */
  function plan(input) {
    if (!input || typeof input !== 'object') return empty('invalid_input');
    var dailyOz = input.dailyOz;
    var days = input.days === undefined ? 30 : input.days;
    var packSize = input.packSize === undefined ? 'value' : input.packSize;
    var shipping = input.shipping === undefined ? false : input.shipping;
    if (!number(dailyOz) || dailyOz <= 0 || dailyOz > MAX_SAFE ||
        !Number.isSafeInteger(days) || days <= 0) return empty('invalid_amount');
    if (packSize !== 'small' && packSize !== 'value') return empty('invalid_pack_size');
    if (typeof shipping !== 'boolean') return empty('invalid_shipping');
    var targetLb = dailyOz / 16 * days;
    if (!number(targetLb) || targetLb <= 0 || targetLb > MAX_SAFE) return empty('invalid_amount');
    var shippingExtraLb = shipping ? Math.max(0, 40 - targetLb) : 0;
    var orderTargetLb = Math.max(targetLb, shipping ? 40 : 0);
    var products = input.products;
    if (!Array.isArray(products) || products.length < 1 || products.length > 2) {
      return empty('invalid_products', targetLb, shippingExtraLb);
    }
    var shareTotal = 0;
    var productIds = [];
    var variantIds = [];
    var prepared = [];
    for (var p = 0; p < products.length; p += 1) {
      var product = products[p];
      if (!product || !identifier(product.id) || productIds.indexOf(String(product.id)) !== -1 ||
          !number(product.share) || product.share <= 0 || product.share > 1 ||
          !Array.isArray(product.variants)) return empty('invalid_products', targetLb, shippingExtraLb);
      productIds.push(String(product.id));
      shareTotal += product.share;
      var variants = product.variants.filter(function (variant) {
        return variant && identifier(variant.id) && variant.available === true &&
          number(variant.lb) && variant.lb > 0 && variant.lb <= MAX_SAFE / MAX_CASES &&
          Number.isSafeInteger(variant.price) && variant.price >= 0;
      });
      if (!variants.length) return empty('unavailable_product', targetLb, shippingExtraLb);
      if (variants.length > 2) return empty('too_many_variants', targetLb, shippingExtraLb);
      for (var v = 0; v < variants.length; v += 1) {
        if (variantIds.indexOf(String(variants[v].id)) !== -1) {
          return empty('duplicate_variant', targetLb, shippingExtraLb);
        }
        variantIds.push(String(variants[v].id));
      }
      prepared.push({ id: product.id, share: product.share, variants: variants });
    }
    if (Math.abs(shareTotal - 1) > EPSILON) return empty('invalid_shares', targetLb, shippingExtraLb);

    var result = empty(null, targetLb, shippingExtraLb);
    for (var i = 0; i < prepared.length; i += 1) {
      var item = prepared[i];
      var selection = choose(item.variants, orderTargetLb * item.share / shareTotal, packSize);
      if (!selection) return empty('quantity_limit', targetLb, shippingExtraLb);
      for (var j = 0; j < item.variants.length; j += 1) {
        if (!selection.quantities[j]) continue;
        result.lines.push({
          productId: item.id,
          variantId: item.variants[j].id,
          quantity: selection.quantities[j],
          lb: item.variants[j].lb,
          price: item.variants[j].price
        });
      }
      result.totalLb += selection.lb;
      result.subtotal += selection.cost;
      if (result.totalLb > MAX_SAFE || !Number.isSafeInteger(result.subtotal)) {
        return empty('unsafe_total', targetLb, shippingExtraLb);
      }
    }
    result.coverageDays = result.totalLb / (dailyOz / 16);
    if (!number(result.coverageDays) || result.coverageDays > MAX_SAFE) {
      return empty('unsafe_total', targetLb, shippingExtraLb);
    }
    return result;
  }

  return { plan: plan };
}));
